import React, { useState, useEffect, useRef } from "react";
import Layout from "../../components/Layout";
import quotesData from "../../data/inspidom-quotes.json";

const InspidomPage = () => {
  const [currentQuote, setCurrentQuote] = useState(null);
  const [count, setCount] = useState(0);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const quoteCardRef = useRef(null);

  const isBrowser = typeof window !== "undefined";
  const canonicalUrl = "https://www.optimsight.com/tools/inspidom";
  const url = isBrowser && !["localhost", "127.0.0.1"].includes(window.location.hostname)
    ? window.location.href
    : canonicalUrl;
  const shareText = currentQuote
    ? `"${currentQuote.quote}" - ${currentQuote.author || "Unknown"}`
    : "Explore Inspidom on Optimsight";
  const shareCopy = `${shareText}\n\nDiscovered via Optimsight Inspidom: ${url}`;

  useEffect(() => {
    getRandomQuote();
  }, []);

  useEffect(() => {
    const closeMenu = (e) => {
      if (!e.target.closest('.share-dropdown')) {
        setIsShareOpen(false);
      }
    };
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, []);

  const getRandomQuote = () => {
    const randomIndex = Math.floor(Math.random() * quotesData.length);
    setCurrentQuote(quotesData[randomIndex]);
    setCount(prev => prev + 1);
    setCopied(false);
    if (quoteCardRef.current) {
      quoteCardRef.current.scrollTop = 0;
    }
  };

  const handleCopy = () => {
    if (!currentQuote) return;
    navigator.clipboard.writeText(shareCopy).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Layout>
      <section className="section inspidom-section">
        <div className="container inspidom-container">
          <div
            ref={quoteCardRef}
            className="inspidom-quote-stage"
            onClick={getRandomQuote}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                getRandomQuote();
              }
            }}
            role="button"
            tabIndex={0}
            title="Click anywhere to get next quote"
          >
            {currentQuote && (
              <div className="has-text-centered inspidom-quote-content">
                <blockquote className="inspidom-quote-block">
                  <p className="title is-size-3 is-size-4-mobile inspidom-quote-text">
                    "{currentQuote.quote}"
                  </p>
                  <cite className="subtitle is-size-5 is-size-6-mobile mt-5 is-block inspidom-quote-author">
                    - {currentQuote.author || "Unknown"}
                  </cite>
                </blockquote>
              </div>
            )}
          </div>

          <div className="is-flex is-justify-content-center is-align-items-center is-flex-wrap-wrap inspidom-actions">
            <button onClick={getRandomQuote} className="button is-primary is-rounded inspidom-next-button">
              Next Inspidom
            </button>
            
            <div className="share-dropdown">
              <button 
                className="share-button" 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsShareOpen(!isShareOpen);
                }}
              >
                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" width="1em" height="1em" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92z"/>
                </svg>
                <span>Share</span>
              </button>
              
              {isShareOpen && (
                <div className="share-menu">
                  <button onClick={() => {
                    navigator.clipboard.writeText(shareCopy);
                    setIsShareOpen(false);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}>
                    <svg viewBox="0 0 24 24" width="20" height="20">
                      <path fill="currentColor" d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                    </svg>
                    Copy Inspidom
                  </button>
                  <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer">
                    <svg viewBox="0 0 24 24" width="20" height="20">
                      <path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    Share on X
                  </a>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer">
                    <svg viewBox="0 0 24 24" width="20" height="20">
                      <path fill="currentColor" d="M9.198 21.5h4v-8.01h3.604l.396-3.98h-4V7.5a1 1 0 0 1 1-1h3v-4h-3a5 5 0 0 0-5 5v2.01h-2l-.396 3.98h2.396v8.01z"/>
                    </svg>
                    Share on Facebook
                  </a>
                  <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer">
                    <svg viewBox="0 0 24 24" width="20" height="20">
                      <path fill="currentColor" d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
                    </svg>
                    Share on LinkedIn
                  </a>
                </div>
              )}
            </div>

            <button onClick={handleCopy} className="share-button">
              {copied ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              )}
              <span>{copied ? "Copied" : "Copy"}</span>
            </button>
          </div>
          
          <div className="has-text-centered inspidom-session">
            <p className="inspidom-session-count">
              <span className="inspidom-session-count-number">{count}</span>
              <span className="inspidom-session-count-label">{count === 1 ? 'Inspidom' : 'Inspidoms'}</span>
              explored this session
            </p>
          </div>

        </div>
      </section>
    </Layout>
  );
};

export default InspidomPage;
